import { Search, X } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { IconButton } from './IconButton'

export interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Rendered as a right-aligned key hint, e.g. "⌘K". Visual only. */
  shortcutHint?: string
  autoFocus?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Search field with a clear button.
 *
 * Debouncing is the caller's job (useDebouncedValue) so a controlled parent
 * keeps its own timing — a component that debounces internally makes the input
 * feel laggy when the parent only needs the value on submit.
 */
export function SearchInput({
  value,
  onChange,
  placeholder,
  shortcutHint,
  autoFocus,
  className,
  'aria-label': ariaLabel,
}: SearchInputProps) {
  const t = useT()
  return (
    <div className={cn('relative w-full', className)}>
      <Search
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
      />
      <input
        type="search"
        role="searchbox"
        aria-label={ariaLabel ?? t('common.search')}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder ?? t('common.searchEllipsis')}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-md border border-border bg-surface pl-9 pr-16 text-base text-fg
          placeholder:text-fg-subtle transition-colors duration-fast hover:border-border-strong
          [&::-webkit-search-cancel-button]:hidden"
      />
      <span className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {value && <IconButton label={t('common.clearSearch')} icon={<X />} size="sm" onClick={() => onChange('')} />}
        {!value && shortcutHint && (
          <kbd className="rounded border border-border bg-surface-muted px-1.5 py-0.5 text-2xs font-medium text-fg-subtle">
            {shortcutHint}
          </kbd>
        )}
      </span>
    </div>
  )
}
